import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Upload, CheckCircle2, Copy } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { useCart } from "../../context/CartContext";
import { ShopHeader } from "../../components/shop/ShopHeader";
import { toast } from "sonner";

const PAYMENTS = [
  { id: "tmoney", label: "Tmoney", desc: "Paiement mobile — sur preuve", cls: "border-tmoney" },
  { id: "flooz", label: "Flooz", desc: "Paiement mobile — sur preuve", cls: "border-flooz" },
  { id: "livraison", label: "Payer à la réception", desc: "Espèces à la réception", cls: "border-brand-accent" },
];

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", customer_address: "", payment_method: "livraison", notes: "", payment_ref: "", payment_proof_path: "", delivery_zone: "", promo_code: "", partner_code: "" });
  const [settings, setSettings] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");
  const [partnerName, setPartnerName] = useState("");

  useEffect(() => {
    api.get("/settings/payment").then(({ data }) => {
      setSettings(data);
      if (data?.paygate_enabled) setForm((f) => ({ ...f, payment_method: "paygate" }));
    }).catch(() => {});
    api.get("/settings/delivery").then(({ data }) => setDelivery(data)).catch(() => {});
    // Parrainage partenaire (?ref= ou mémorisé)
    const ref = new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("noumabou_ref");
    if (ref) {
      const code = ref.toUpperCase();
      api.get(`/partners/validate/${code}`).then(({ data }) => {
        if (data.valid) { setForm((f) => ({ ...f, partner_code: code })); setPartnerName(data.name); }
      }).catch(() => {});
    }
  }, []);

  const paygateEnabled = settings?.paygate_enabled;
  const paymentOptions = paygateEnabled
    ? [
        { id: "paygate", label: "Payer en ligne — Flooz / T-Money", desc: "Paiement mobile sécurisé (recommandé)", cls: "border-brand" },
        { id: "livraison", label: "Payer à la réception", desc: "Espèces à la réception", cls: "border-brand-accent" },
      ]
    : PAYMENTS;

  const discount = promo?.discount || 0;
  const grandTotal = Math.max(total - discount, 0);

  const applyPromo = async () => {
    setPromoMsg("");
    if (!form.promo_code.trim()) return;
    try {
      const { data } = await api.post("/promo/validate", { code: form.promo_code.trim(), subtotal: total });
      if (data.valid) { setPromo(data); setPromoMsg(data.message); }
      else { setPromo(null); setPromoMsg(data.message); }
    } catch { setPromo(null); setPromoMsg("Erreur de vérification du code."); }
  };
  const waNumber = (delivery?.whatsapp_number || "").replace(/\D/g, "");
  const waMessage = encodeURIComponent(
    `Bonjour NOUMABOU, je souhaite commander :\n${items.map((i) => `- ${i.qty} x ${i.name}`).join("\n")}\nTotal produits : ${fcfa(total)}\nMa zone de livraison : (à préciser)\nMon nom : ${form.customer_name || "..."}`
  );
  const waLink = `https://wa.me/${waNumber}?text=${waMessage}`;

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/payment-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, payment_proof_path: data.path }));
      toast.success("Capture ajoutée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="App"><ShopHeader />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="text-brand-muted">Votre panier est vide.</p>
          <button onClick={() => navigate("/boutique")} className="mt-6 bg-brand text-white rounded-full px-6 py-3 font-semibold">Voir les produits</button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name || form.customer_phone.replace(/\D/g, "").replace(/^228/, "").length < 8) {
      setError("Le nom et un numéro de téléphone valide (8 chiffres) sont obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const itemsPayload = items.map((i) => ({ product_id: i.product_id, qty: i.qty }));
      if (form.payment_method === "paygate") {
        const { data } = await api.post("/payments/paygate/initiate", {
          ...form, items: itemsPayload, origin_url: window.location.origin,
        });
        clear();
        window.location.href = data.payment_url;
        return;
      }
      const { data } = await api.post("/orders", { ...form, items: itemsPayload });
      clear();
      toast.success("Commande confirmée !");
      navigate(`/confirmation/${data.order_number}`, { state: { total: data.total } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "border border-brand-line rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all bg-white text-brand-ink";

  return (
    <div className="App overflow-x-hidden min-h-screen bg-brand-cream">
      <ShopHeader />
      <div className="w-full max-w-md mx-auto sm:max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/panier")} className="flex items-center gap-2 text-brand-sub hover:text-brand mb-6 font-medium">
          <ArrowLeft className="w-4 h-4" /> Retour au panier
        </button>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink mb-6">Finaliser la commande</h1>

        <div className="grid sm:grid-cols-5 gap-6">
          <form onSubmit={submit} className="sm:col-span-3 space-y-4 bg-white border border-brand-line rounded-2xl p-6">
            <div>
              <label className="text-sm font-medium text-brand-sub">Nom complet *</label>
              <input data-testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} className={inputCls + " mt-1"} placeholder="Ex: Koffi Mensah" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-sub">Téléphone *</label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-brand-line bg-brand-cream text-brand-sub text-sm font-semibold" data-testid="phone-prefix">+228</span>
                <input data-testid="checkout-phone" value={form.customer_phone.replace(/^\+228\s?/, "")}
                  onChange={(e) => setForm((f) => ({ ...f, customer_phone: "+228 " + e.target.value.replace(/^\+?228\s?/, "") }))}
                  className={inputCls + " rounded-l-none"} placeholder="90 00 00 00" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-sub">E-mail (pour la confirmation)</label>
              <input data-testid="checkout-email" type="email" value={form.customer_email} onChange={set("customer_email")} className={inputCls + " mt-1"} placeholder="Ex: vous@email.com (optionnel)" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-sub">Adresse (pour vous contacter)</label>
              <input data-testid="checkout-address" value={form.customer_address} onChange={set("customer_address")} className={inputCls + " mt-1"} placeholder="Quartier, repère, ville" />
            </div>

            <div>
              <label className="text-sm font-medium text-brand-sub mb-2 block">Mode de paiement</label>
              <div className="grid grid-cols-1 gap-2">
                {paymentOptions.map((p) => (
                  <button type="button" key={p.id} data-testid={`payment-${p.id}`} onClick={() => setForm({ ...form, payment_method: p.id })}
                    className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${form.payment_method === p.id ? p.cls + " bg-brand-cream" : "border-brand-line"}`}>
                    <span className="font-semibold text-brand-ink">{p.label}</span>
                    <span className="block text-xs text-brand-muted">{p.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {(form.payment_method === "tmoney" || form.payment_method === "flooz") && settings && (
              <div data-testid="mobile-money-panel" className="rounded-xl border-2 border-brand-line bg-brand-cream p-4 space-y-3">
                <p className="text-sm text-brand-ink font-semibold">Paiement {form.payment_method === "tmoney" ? "Tmoney" : "Flooz"}</p>
                <div className="text-sm text-brand-sub space-y-1">
                  <p>Bénéficiaire : <span className="font-semibold text-brand-ink">{settings.beneficiary_name || "Noumabou"}</span></p>
                  {(form.payment_method === "tmoney" ? settings.tmoney_number : settings.flooz_number) ? (
                    <p className="flex items-center gap-2">
                      Numéro :
                      <span data-testid="pay-number" className="font-semibold text-brand-ink">{form.payment_method === "tmoney" ? settings.tmoney_number : settings.flooz_number}</span>
                      <button type="button" onClick={() => { navigator.clipboard?.writeText(form.payment_method === "tmoney" ? settings.tmoney_number : settings.flooz_number); toast.success("Numéro copié"); }} className="p-1 hover:bg-white rounded"><Copy className="w-3.5 h-3.5" /></button>
                    </p>
                  ) : (
                    <p className="text-brand-muted italic">Numéro à confirmer — contactez-nous après paiement.</p>
                  )}
                  <p className="font-semibold text-brand-ink">Montant : {fcfa(total)}</p>
                </div>
                <p className="text-xs text-brand-muted">{settings.instructions}</p>
                <input data-testid="payment-ref" value={form.payment_ref} onChange={(e) => setForm({ ...form, payment_ref: e.target.value })} className="border border-brand-line rounded-xl px-4 py-2.5 w-full bg-white text-brand-ink outline-none focus:ring-2 focus:ring-brand-accent" placeholder="Référence / ID de la transaction" />
                <div>
                  <label className="text-sm font-medium text-brand-sub mb-1.5 block">Capture d'écran du paiement</label>
                  {form.payment_proof_path ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Capture ajoutée
                      <button type="button" onClick={() => setForm({ ...form, payment_proof_path: "" })} className="text-brand-secondary text-xs underline ml-2">retirer</button>
                    </div>
                  ) : (
                    <label data-testid="upload-proof" className="flex items-center gap-2 cursor-pointer bg-white border border-dashed border-brand-line rounded-xl px-4 py-3 text-sm text-brand-sub hover:bg-brand-cream">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? "Envoi…" : "Joindre une image (png, jpg)"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadProof} disabled={uploading} />
                    </label>
                  )}
                </div>
              </div>
            )}

            {error && <p data-testid="checkout-error" className="text-sm text-brand-secondary">{error}</p>}

            <button type="submit" disabled={loading} data-testid="place-order-button" className="w-full bg-brand text-white hover:bg-brand-hover transition-colors rounded-full px-6 py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Confirmer — ${fcfa(grandTotal)}`}
            </button>
          </form>

          <div className="sm:col-span-2">
            <div className="bg-white border border-brand-line rounded-2xl p-6 sticky top-20">
              <h3 className="font-heading font-semibold text-brand-ink mb-4">Récapitulatif</h3>
              <div className="space-y-3">
                {items.map((i) => (
                  <div key={i.product_id} className="flex justify-between text-sm">
                    <span className="text-brand-sub">{i.qty} × {i.name}</span>
                    <span className="font-medium text-brand-ink">{fcfa(i.price * i.qty)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-brand-line mt-4 pt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-brand-sub">Sous-total</span><span className="text-brand-ink">{fcfa(total)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between" data-testid="recap-discount"><span className="text-green-700">Remise ({promo.code})</span><span className="text-green-700 font-semibold">-{fcfa(discount)}</span></div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-brand-line">
                {partnerName && (
                  <div data-testid="referral-banner" className="mb-3 rounded-xl bg-brand-cream px-3 py-2 text-xs text-brand-ink flex items-center gap-1.5">
                    🤝 Parrainé par <b>{partnerName}</b>
                  </div>
                )}
                <label className="text-xs font-medium text-brand-sub">Code promo</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="promo-input" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} className="border border-brand-line rounded-xl px-3 py-2 flex-1 text-sm bg-white text-brand-ink outline-none focus:ring-2 focus:ring-brand-accent" placeholder="Ex: NOUMABOU10" />
                  <button type="button" onClick={applyPromo} data-testid="apply-promo-button" className="bg-brand-ink text-white rounded-xl px-4 py-2 text-sm font-semibold shrink-0 hover:opacity-90">Appliquer</button>
                </div>
                {promoMsg && <p className={`text-xs mt-1.5 ${promo ? "text-green-700" : "text-brand-secondary"}`} data-testid="promo-msg">{promoMsg}</p>}
              </div>
              <div className="border-t border-brand-line mt-3 pt-3 flex justify-between">
                <span className="font-semibold text-brand-ink">Total</span>
                <span className="font-heading font-black text-xl text-brand-ink" data-testid="recap-total">{fcfa(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
